import Konva from "konva";
import React from "react";
import { LayerType, Shape, SpotShape } from "../../../types";
import {
  useMapCanvas,
  useMapCanvasInteraction,
  useMapCanvasShapes
} from "../contexts/MapCanvasContext";
import {
  useMapEditorActions,
  useMapEditorSelection,
  useMapEditorTool,
  useMapEditorUI
} from "../contexts/MapEditorContext";
import { MapEditorState } from "../contexts/MapEditorContext/MapEditorContext.types";
import { DraggableShape } from "../shapes/DraggableShape/DraggableShape";
import { ShapeProvider } from "../shapes/ShapeContext/ShapeProvider";
import { EditorSnapConfig } from "../shapes/ShapeContext/ShapeProvider.types";
import { ShapeEditingHighlightOptions } from "../types";

export type UseGetEditorShapesByTypeReturn = (
  type: LayerType
) => React.ReactNode;

/**
 * Holds references to editor callbacks that change frequently.
 * Using a ref allows memoized child components to always access
 * the latest callbacks without receiving new prop references.
 */
interface EditorCallbacksRef {
  selectedIds: string[];
  shapes: Shape[];
  /** Snap candidates: the shapes, minus zones while they are hidden. */
  snapShapes: Shape[];
  wallShapes: Shape[];
  tool: string;
  onShapeUpdate: (id: string, props: Partial<Shape>) => void;
  onDeleteShape: (id: string | string[]) => void;
  onLocateShape: (id: string) => void;
  onMoveShape: (id: string, direction: "up" | "down") => void;
  onSelectionChange: (ids: string[]) => void;
  shouldPreventDeselect: React.MutableRefObject<boolean>;
  setContextMenuShape: MapEditorState["setContextMenuShape"];
  setHighlightedShape: (highlight: ShapeEditingHighlightOptions | null) => void;
  setNodeStyleToolTarget: (target: SpotShape | null) => void;
  nodeStyleToolTarget: SpotShape | null;
  copyNodeStyleToNodeTarget: (source: string, target: string) => void;
  duplicateShape: (
    id: string | string[],
    options?: {
      offset?: { x: number; y: number };
      selectDuplicates?: boolean;
      switchResourceId?: boolean;
      switchIndex?: boolean;
      override?:
        | Partial<Shape>
        | (Partial<Shape> & Required<Pick<Shape, "id">>)[];
    }
  ) => Shape[] | undefined;
}

interface EditorShapeItemProps {
  shape: Shape;
  isSelected: boolean;
  isInSelection: boolean;
  selectIndex: number | undefined;
  disabled: boolean;
  isMultiSelected: boolean;
  highlightedShape: ShapeEditingHighlightOptions | null;
  editorRef: React.RefObject<EditorCallbacksRef>;
  snapConfigRef: React.RefObject<EditorSnapConfig>;
  isDragging: boolean;
}

const EditorShapeItem = React.memo<EditorShapeItemProps>(
  ({
    shape,
    isSelected,
    isInSelection,
    selectIndex,
    disabled,
    isMultiSelected,
    highlightedShape,
    editorRef,
    snapConfigRef,
    isDragging
  }) => {
    const ed = editorRef.current;
    const otherShapes =
      shape.type === "wall" || shape.type === "wall-path"
        ? ed.wallShapes
        : ed.snapShapes;

    const handleSelect = (
      e: Konva.KonvaEventObject<MouseEvent | TouchEvent>
    ) => {
      const ed = editorRef.current;
      if (shape.type === "spot") {
        if (ed.tool === "node-style") {
          if (
            ed.nodeStyleToolTarget &&
            ed.nodeStyleToolTarget.id !== shape.id
          ) {
            ed.copyNodeStyleToNodeTarget(ed.nodeStyleToolTarget.id, shape.id);
          } else {
            ed.setNodeStyleToolTarget(shape as SpotShape);
          }
          return;
        }
      }
      if (ed.tool === "select" && !e.evt.altKey) {
        e.cancelBubble = true;
        ed.shouldPreventDeselect.current = true;

        const isMultiSelect = e.evt.shiftKey;

        if (isMultiSelect) {
          const currentSelectedIds = ed.selectedIds;
          const newSelectedIds = currentSelectedIds.includes(shape.id)
            ? currentSelectedIds.filter((selectedId) => selectedId !== shape.id)
            : [...currentSelectedIds, shape.id];
          ed.onSelectionChange(newSelectedIds);
        } else {
          ed.onSelectionChange([shape.id]);
        }
      }
    };

    return (
      <ShapeProvider
        shape={shape}
        isSelected={isSelected}
        isInSelection={isInSelection}
        onSelect={handleSelect}
        onUpdate={(newProps) =>
          editorRef.current.onShapeUpdate(shape.id, newProps)
        }
        editorConfigRef={snapConfigRef}
        otherShapes={otherShapes}
        disabled={disabled}
        isMultiSelected={isMultiSelected}
        onDelete={() => editorRef.current.onDeleteShape(shape.id)}
        onLocate={() => editorRef.current.onLocateShape(shape.id)}
        onMoveUp={() => editorRef.current.onMoveShape(shape.id, "up")}
        onMoveDown={() => editorRef.current.onMoveShape(shape.id, "down")}
        selectIndex={selectIndex}
      >
        <DraggableShape
          isDragging={isDragging}
          setContextMenuShape={ed.setContextMenuShape}
          setHighlightedShape={ed.setHighlightedShape}
          highlightedShape={highlightedShape}
          onStartDragDuplicate={() => {
            editorRef.current.duplicateShape(shape.id, {
              offset: { x: 0, y: 0 },
              selectDuplicates: false,
              switchResourceId: true,
              switchIndex: true
            });
          }}
        />
      </ShapeProvider>
    );
  },
  (prev, next) => {
    if (prev.shape !== next.shape) return false;
    if (prev.isSelected !== next.isSelected) return false;
    if (prev.isInSelection !== next.isInSelection) return false;
    if (prev.selectIndex !== next.selectIndex) return false;
    if (prev.disabled !== next.disabled) return false;
    // Only re-render selected shapes when isMultiSelected changes
    if (
      prev.isMultiSelected !== next.isMultiSelected &&
      (next.isSelected || prev.isSelected)
    )
      return false;
    if (prev.isDragging !== next.isDragging) return false;

    // Only re-render for highlightedShape if this shape is involved
    const prevHighlighted = prev.highlightedShape?.shape?.id === prev.shape.id;
    const nextHighlighted = next.highlightedShape?.shape?.id === next.shape.id;
    if (prevHighlighted !== nextHighlighted) return false;
    if (nextHighlighted && prev.highlightedShape !== next.highlightedShape)
      return false;

    return true;
  }
);

EditorShapeItem.displayName = "EditorShapeItem";

export const useGetEditorShapesByType = (): UseGetEditorShapesByTypeReturn => {
  const { selectedIds } = useMapEditorSelection();
  const { activeTool: tool } = useMapEditorTool();
  const {
    snapToGrid,
    showSnapLines,
    highlightedShape,
    nodeStyleToolTarget,
    showZones
  } = useMapEditorUI();
  const {
    moveShape: onMoveShape,
    deleteShape: onDeleteShape,
    locateShape: onLocateShape,
    updateShape: onShapeUpdate,
    setSelectedIds: onSelectionChange,
    shouldPreventDeselect,
    setContextMenuShape,
    setHighlightedShape,
    setNodeStyleToolTarget,
    copyNodeStyleToNodeTarget,
    duplicateShape
  } = useMapEditorActions();
  const { shapes } = useMapCanvasShapes();
  const { gridSize } = useMapCanvas();
  const { isAltPressed, shapesInSelection, isDragging } =
    useMapCanvasInteraction();

  // O(1) lookups instead of O(n) .includes() per shape
  const selectedIdsSet = React.useMemo(
    () => new Set(selectedIds),
    [selectedIds]
  );
  const shapesInSelectionSet = React.useMemo(
    () => new Set(shapesInSelection),
    [shapesInSelection]
  );

  // O(1) shape lookup by id instead of O(n) .find() per selectedId
  const shapesById = React.useMemo(
    () => new Map(shapes.map((s) => [s.id, s])),
    [shapes]
  );

  // Pre-group shapes by layer type — avoids filtering all shapes 4 times per render.
  // Zones step aside here while the Zones panel has them switched off, so no
  // shape needs to read the UI context for it.
  const shapesByLayer = React.useMemo(() => {
    const map = new Map<LayerType, Shape[]>();
    for (const shape of shapes) {
      if (shape.type === "zone" && !showZones) continue;
      const list = map.get(shape.layerType);
      if (list) {
        list.push(shape);
      } else {
        map.set(shape.layerType, [shape]);
      }
    }
    return map;
  }, [shapes, showZones]);

  // Hidden zones neither render nor attract snapping.
  const snapShapes = React.useMemo(
    () => (showZones ? shapes : shapes.filter((s) => s.type !== "zone")),
    [shapes, showZones]
  );

  // Pre-compute wall/wall-path shapes so each wall doesn't re-filter the entire shapes array
  const wallShapes = React.useMemo(
    () =>
      shapes.filter(
        (s) => (s.type === "wall" || s.type === "wall-path") && !s.hidden
      ),
    [shapes]
  );

  // Store all callbacks in a ref so memoized children always access latest versions
  const editorRef = React.useRef<EditorCallbacksRef>(null!);
  editorRef.current = {
    selectedIds,
    shapes,
    snapShapes,
    wallShapes,
    tool,
    onShapeUpdate,
    onDeleteShape,
    onLocateShape,
    onMoveShape,
    onSelectionChange,
    shouldPreventDeselect,
    setContextMenuShape,
    setHighlightedShape,
    setNodeStyleToolTarget,
    nodeStyleToolTarget,
    copyNodeStyleToNodeTarget,
    duplicateShape
  };

  // Snap config ref — stable identity, updated every render.
  // DraggableShape/GroupShape read .current during drag events.
  const snapConfigRef = React.useRef<EditorSnapConfig>({
    snapToGrid,
    gridSize,
    showSnapLines
  });
  snapConfigRef.current = { snapToGrid, gridSize, showSnapLines };

  const shapeSelectionIndex = React.useMemo(
    () =>
      selectedIds.reduce(
        (acc, shapeId) => {
          const shape = shapesById.get(shapeId);
          if (shape?.type === "spot") {
            acc[shapeId] = Object.keys(acc).length;
          }
          return acc;
        },
        {} as Record<string, number>
      ),
    [selectedIds, shapesById]
  );

  const disabled = tool !== "select" || isAltPressed;
  const isMultiSelected = selectedIds.length > 1;

  return (type: LayerType) => {
    return (shapesByLayer.get(type) || []).map((shape) => (
      <EditorShapeItem
        key={shape.id}
        shape={shape}
        isSelected={selectedIdsSet.has(shape.id)}
        isInSelection={shapesInSelectionSet.has(shape.id)}
        selectIndex={
          isMultiSelected ? shapeSelectionIndex[shape.id] : undefined
        }
        disabled={disabled}
        isMultiSelected={isMultiSelected}
        highlightedShape={highlightedShape}
        editorRef={editorRef}
        snapConfigRef={snapConfigRef}
        isDragging={isDragging}
      />
    ));
  };
};
